
import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { cleanupAuthState, getMFAFactors } from '@/utils/authUtils';
import { createOrUpdateUser } from '@/services/supabase/dataService';
import { useToast } from '@/hooks/use-toast';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  mfaEnabled: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: Error, mfaRequired?: boolean }>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error?: Error, emailVerificationRequired?: boolean, user?: User }>;
  signOut: () => Promise<void>;
  checkMfaEnabled: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const { toast } = useToast();
  
  const checkMfaEnabled = async () => {
    if (!user) return false;
    
    try {
      const { totpEnabled } = await getMFAFactors();
      setMfaEnabled(totpEnabled);
      return totpEnabled;
    } catch (error) {
      console.error("Error checking MFA status:", error);
      return false;
    }
  };
  
  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state change event:", event, "User:", session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN' && session?.user) {
          // Defer MFA check to prevent deadlocks
          setTimeout(() => {
            checkMfaEnabled();
          }, 0);
        }
        
        if (event === 'SIGNED_OUT') {
          setMfaEnabled(false);
        }
        
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session check:", session ? `Session found for ${session.user.email}` : "No session");
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Defer MFA check to prevent deadlocks
        setTimeout(() => {
          checkMfaEnabled();
        }, 0);
      }
      
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log("Starting sign in process for:", email);
      setIsLoading(true);
      // Clean up auth state before signing in
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
        console.error("Error during global sign out:", err);
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error("Sign in error:", error);
        throw error;
      }
      
      // Success! We now allow users to sign in regardless of email verification status
      console.log("Sign in successful, user:", data.user?.id, data.user?.email);
      
      // Check if MFA is required
      const { totpEnabled } = await getMFAFactors();
      if (totpEnabled) {
        console.log("MFA is required for this account");
        return { mfaRequired: true };
      }
      
      return {};
    } catch (error) {
      console.error("Sign in process error:", error);
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      setIsLoading(true);
      console.log(`Starting sign up process for: ${email}, ${firstName} ${lastName}`);
      
      // Clean up auth state before signing up
      cleanupAuthState();
      
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName
          }
        }
      });
      
      if (error) {
        console.error("Sign up error from Supabase:", error);
        throw error;
      }
      
      console.log("Sign up API response:", data);
      
      // Important: Save user to the public.users table regardless of email confirmation
      if (data.user) {
        try {
          console.log("Saving user data to public.users table:", email, firstName, lastName);
          const userId = await createOrUpdateUser(email, firstName, lastName);
          console.log("User saved to database with ID:", userId);
          
          // Add success toast
          toast({
            title: "Account created",
            description: "User profile created successfully",
            variant: "default",
          });
        } catch (dbError) {
          console.error("Failed to save user data to database:", dbError);
          // We continue the flow despite DB errors to ensure the auth signup isn't blocked
          toast({
            title: "Account created with issues",
            description: "Account created but profile data couldn't be saved. This might affect some functionality.",
            variant: "destructive",
          });
        }
      }
      
      // Email verification is always considered required for the UI flow
      // But the user data is now being saved to the database regardless
      if (data.user) {
        return { emailVerificationRequired: true, user: data.user };
      }
      
      return {};
    } catch (error) {
      console.error("Sign up process error:", error);
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      console.log("Starting sign out process");
      
      // Clean up auth state
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
        console.error("Error during sign out:", err);
      }
      
      // Force page reload for a clean state
      console.log("Sign out complete, redirecting to login page");
      window.location.href = '/login';
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    mfaEnabled,
    signIn,
    signUp,
    signOut,
    checkMfaEnabled
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
